import { getDurationBucket, getViewDuration } from '../selectors/telemetrySelectors';
import { logUsage } from 'owa-analytics-actions';
import { lazyResetCalendarView } from 'owa-calendar-list';
import { closeNotesEditor } from 'owa-calendar-notes-editor';
import { lazyUpdateSelectedEventId } from 'owa-event-details';
import { lazyInitializeConflictsView } from 'owa-conflicts-view';
import { LazyInitializeQuickAdd } from 'owa-todos';
import { orchestrator } from 'satcheljs';
import { openTimePanel, popView } from 'owa-time-panel-bootstrap';
import {
    preloadTimePanelData,
    openTasksView,
    openCalendarView,
    openEventDetailsView,
    openAttendeeTrackingView,
    closeAttendeeTrackingView,
    openConflictsView,
    closeConflictsView,
} from '../actions/publicActions';
import {
    getSelectedCalendarItemId,
    getTimePanelSource,
} from '../selectors/timePanelStoreSelectors';
import {
    getTimePanelCustomData,
    TimePanelChangeViewCustomData,
    timePanelOptions,
} from '../datapoints';
import {
    panelClosed,
    panelOpened,
    pushNewView,
    updateSelectedCalendarEvent,
    updateCurrentView,
    setConflictViewState,
    resetPanelViewOpenTimeStamp,
} from '../actions/timePanelStoreActions';
import {
    popTimePanelView,
    pushTimePanelView,
    updateTopTimePanelView,
    PanelView,
    getPreviousPanelView,
    getPanelViewType,
    getStackLength,
    getCurrentPanelView,
    isShowingConflictsView,
    isShowingAttendeeTrackingView,
} from 'owa-time-panel-view-stack';
import { toggleTimePanel } from '../utils/toggleTimePanel';

// Orchestrators for public actions

export const preloadTimePanelDataOrchestrator = orchestrator(preloadTimePanelData, async () => {
    await loadPanelData();
});

export const openEventDetailsViewOrchestrator = orchestrator(
    openEventDetailsView,
    actionMessage => {
        const { source, clientItemId } = actionMessage;
        // make sure panel is open
        openTimePanel(source);
        // close all non-base views
        while (getStackLength() > 1) {
            popView();
        }
        // open directly to EventDetails view
        updateSelectedCalendarEvent(clientItemId);
        pushNewView('EventDetails');
    }
);

export const openAttendeeTrackingViewOrchestrator = orchestrator(
    openAttendeeTrackingView,
    actionMessage => {
        const { source, clientItemId } = actionMessage;
        // make sure panel is open
        openTimePanel(source);
        // close all non-base views
        while (getStackLength() > 1) {
            popView();
        }
        // open EventDetails for the event and then AttendeeTracking
        // so that back will return to the event
        updateSelectedCalendarEvent(clientItemId);
        pushNewView('EventDetails');
        pushNewView('AttendeeTracking');
    }
);

export const closeAttendeeTrackingViewOrchestrator = orchestrator(
    closeAttendeeTrackingView,
    actionMessage => {
        const selectedId = getSelectedCalendarItemId();
        if (selectedId && selectedId.Id == actionMessage.clientItemId.Id) {
            // close the attendee tracking view and the event details view if they are still open for this event
            if (isShowingAttendeeTrackingView()) {
                popView();
                popView();
            }

            // Close the Time Panel if it was opened by MeetingCard and tracking view was still showing
            if (getTimePanelSource() === 'MeetingCard') {
                toggleTimePanel();
            }
        }
    }
);

/**
 * Opens time panel in the calendar view irrespective of the view it is in and was in previously
 */
export const openCalendarViewOrchestrator = orchestrator(openCalendarView, actionMessage => {
    // make sure panel is open
    openTimePanel(actionMessage.source);

    // close all non-base views
    while (getStackLength() > 1) {
        popView();
    }
    // open directly to Calendar view
    updateCurrentView('Calendar');
});

export const openTasksViewOrchestrator = orchestrator(openTasksView, actionMessage => {
    // make sure panel is open
    openTimePanel(actionMessage.source);

    // close all non-base views
    while (getStackLength() > 1) {
        popView();
    }
    // open directly to Tasks view
    updateCurrentView('Tasks');

    const { taskSubject, taskLinkedEmailId, extractionId } = actionMessage;

    // pre-populate Task panel with detected task intent. Continue to show Task panel if the info is not available
    if (taskSubject && taskLinkedEmailId) {
        LazyInitializeQuickAdd.importAndExecute(taskSubject, taskLinkedEmailId, extractionId);
    }
});

export const closeConflictsViewOrchestrator = orchestrator(closeConflictsView, () => {
    // close all views opened from the last conflict view
    while (isShowingConflictsView()) {
        popView();
    }

    // Close the Time Panel only if it was not open before entering Conflicts mode.
    if (getTimePanelSource() === 'ShowConflicts') {
        toggleTimePanel();
    }
});

export const openConflictsViewOrchestrator = orchestrator(openConflictsView, actionMessage => {
    const { source, meeting } = actionMessage;
    openTimePanel(source);
    lazyInitializeConflictsView.importAndExecute(meeting);

    pushNewView('Conflicts');
});

// Core orchestrators

export const panelOpenedOrchestrator = orchestrator(panelOpened, () => {
    // log change view event since it will otherwise not be captured by standard lifecycle
    logChangeView(getCurrentPanelView(), true);
});

export const panelClosedOrchestrator = orchestrator(panelClosed, () => {
    // log change view event since it will otherwise not be captured by standard lifecycle
    logChangeView(null);
    setConflictViewState(null, null);
    // clean up the view stack
    while (getStackLength() > 1) {
        popTimePanelView();
    }
    // clean up notes editor in case an event details view is buried somewhere in the stack
    const id = getSelectedCalendarItemId();
    if (id) {
        closeNotesEditor(id);
    }
    // reset calendar view for next session
    lazyResetCalendarView.importAndExecute();
});

// Orchestrators for view stack

export const pushNewViewOrchestrator = orchestrator(pushNewView, actionMessage => {
    const { newView } = actionMessage;
    logChangeView(newView);
    pushTimePanelView(newView);
});

export const updateCurrentViewOrchestrator = orchestrator(updateCurrentView, actionMessage => {
    const { updatedView } = actionMessage;
    logChangeView(updatedView);
    updateTopTimePanelView(updatedView);
});

export const popViewOrchestrator = orchestrator(popView, () => {
    logChangeView(getPreviousPanelView());
    popTimePanelView();
});

orchestrator(popTimePanelView, updateTimeStamp);
orchestrator(updateTopTimePanelView, updateTimeStamp);
orchestrator(pushTimePanelView, updateTimeStamp);

function updateTimeStamp() {
    resetPanelViewOpenTimeStamp();
}

// Orchestrators for shared event callbacks

export const updateSelectedCalendarEventOrchestrator = orchestrator(
    updateSelectedCalendarEvent,
    actionMessage => {
        const { selectedCalendarEventId } = actionMessage;
        lazyUpdateSelectedEventId.importAndExecute(selectedCalendarEventId);
    }
);

// Helper functions

function logChangeView(nextView: PanelView, isInitialView?: boolean): void {
    // current view will become the previous view (unless this is the initial view on panel load)
    const previousView = isInitialView ? null : getCurrentPanelView();
    const previousViewDuration = isInitialView ? 0 : getViewDuration();
    const previousViewDurationBucket = getDurationBucket(previousViewDuration);
    logUsage(
        'timePanelActionChangeView',
        getTimePanelCustomData<TimePanelChangeViewCustomData>({
            // standard logging
            previousView,
            previousViewType: getPanelViewType(previousView),
            previousViewDuration,
            nextView,
            nextViewType: getPanelViewType(nextView),
            // long-term logging
            owa_1: previousView,
            owa_2: getPanelViewType(previousView),
            owa_3: previousViewDurationBucket.lowerBoundMinutes,
            owa_4: previousViewDurationBucket.upperBoundMinutes,
            owa_5: nextView,
            owa_6: getPanelViewType(nextView),
        }),
        timePanelOptions
    );
}

async function loadPanelData(): Promise<void> {
    // VSO 46502: any prefetch we can do to pre-load agenda view data should be done here, including calling pre-fetch functions from mogenda
}
